/**
 * Session storage, isolated per app namespace.
 *
 * Each web app declares its namespace via `REACT_APP_APP_NAMESPACE`
 * (`admin` for the merchant panel, `backoffice` for the internal panel) so the
 * two apps never share tokens even when served from the same origin.
 */

export type AuthNamespace = 'admin' | 'backoffice';

const KEY_PREFIX = '@KardumeUrbano';

let runtimeNamespace: AuthNamespace | null = null;

const configuredNamespace = (): AuthNamespace | null => {
  const ns = process.env.REACT_APP_APP_NAMESPACE;
  return ns === 'admin' || ns === 'backoffice' ? ns : null;
};

export const setAppNamespace = (namespace: AuthNamespace): void => {
  runtimeNamespace = namespace;
};

export const getNamespace = (): AuthNamespace =>
  runtimeNamespace || configuredNamespace() || 'admin';

const keys = (namespace: AuthNamespace = getNamespace()) => ({
  token: `${KEY_PREFIX}:${namespace}:token`,
  refreshToken: `${KEY_PREFIX}:${namespace}:refreshToken`,
  user: `${KEY_PREFIX}:${namespace}:user`,
});

export const session = {
  getToken(namespace?: AuthNamespace): string | null {
    return localStorage.getItem(keys(namespace).token);
  },
  getRefreshToken(namespace?: AuthNamespace): string | null {
    return localStorage.getItem(keys(namespace).refreshToken);
  },
  getUser<T = unknown>(namespace?: AuthNamespace): T | null {
    const raw = localStorage.getItem(keys(namespace).user);
    return raw ? (JSON.parse(raw) as T) : null;
  },
  set(token: string, refreshToken: string, user: unknown, namespace?: AuthNamespace): void {
    const k = keys(namespace);
    localStorage.setItem(k.token, token);
    localStorage.setItem(k.refreshToken, refreshToken);
    localStorage.setItem(k.user, JSON.stringify(user));
  },
  updateUser(user: unknown, namespace?: AuthNamespace): void {
    localStorage.setItem(keys(namespace).user, JSON.stringify(user));
  },
  clear(namespace?: AuthNamespace): void {
    const k = keys(namespace);
    localStorage.removeItem(k.token);
    localStorage.removeItem(k.refreshToken);
    localStorage.removeItem(k.user);
  },
  isAuthenticated(namespace?: AuthNamespace): boolean {
    return !!this.getToken(namespace);
  },
};
